import type { OasisValidationError } from '../../services/api/types.gen';
import type { CmsCasting } from './cmsCasting';
import { cmsErrorIsDiagnosisSeverity } from './contradictions/scrubberAdvisory';
import type { OASISSchema, Question } from './types';
import { FIX_FIELD_PARAM } from '../../utils/focusFixField';

/**
 * Where a CMS error is fixed, carried in the chart's URL so an error listed
 * outside the chart (the OASIS Manager) can open the chart at the question.
 */
export interface ChartFixTarget {
  qids: string[];
  /** CMS item ids, e.g. M0069_PAT_GENDER, resolved to questions by the chart. */
  items: string[];
  /** Diagnosis severity: fixed in the Active Diagnosis module, not a question. */
  diagnoses: boolean;
}

const QIDS = 'fixQids';
const ITEMS = 'fixItems';
const DIAGNOSES = 'fixDiagnoses';

/** CMS header items the export reads from the clinic's settings. */
const CLINIC_SETTING_ITEMS = new Set([
  'HHA_AGENCY_ID',
  'NATL_PRVDR_ID',
  'STATE_CD',
]);
/** Name, state, ZIP, SSN, birth date: the export takes the patient record's. */
const PATIENT_RECORD_ITEM = /^M00(40|50|60|64|66)(_|$)/;
/** Diagnosis codes and severities: the export takes the visit's diagnoses. */
const DIAGNOSIS_ITEM = /^M102[13](_|$)/;
const CHART_ITEM = /^[A-Z]{1,2}\d{4}/;

/** Where a CMS item is fixed; `none` for one no screen writes (SFW_ID). */
export type CmsItemHome = 'clinic' | 'patient' | 'diagnoses' | 'chart' | 'none';

export function cmsItemHome(item: string): CmsItemHome {
  if (CLINIC_SETTING_ITEMS.has(item)) return 'clinic';
  if (PATIENT_RECORD_ITEM.test(item)) return 'patient';
  if (DIAGNOSIS_ITEM.test(item)) return 'diagnoses';
  return CHART_ITEM.test(item) ? 'chart' : 'none';
}

/** Where in the chart a readiness error is fixed, or null when nowhere is known. */
export function chartFixTargetForError(
  error: Pick<OasisValidationError, 'qids' | 'oasis_fields' | 'edit_id'>
): ChartFixTarget | null {
  const fields = error.oasis_fields ?? [];
  const target = {
    qids: error.qids ?? [],
    items: fields.filter(field => cmsItemHome(field) === 'chart'),
    diagnoses:
      cmsErrorIsDiagnosisSeverity(error) ||
      fields.some(field => cmsItemHome(field) === 'diagnoses'),
  };
  return target.qids.length || target.items.length || target.diagnoses
    ? target
    : null;
}

/** The input that edits each CMS item on Patient Info or in clinic settings. */
const FIELD_FOR_ITEM: [RegExp, string][] = [
  [/^M0040_PAT_FNAME$/, 'first_name'],
  [/^M0040_PAT_LNAME$/, 'last_name'],
  [/^M0040_PAT_MI$/, 'middle_name'],
  [/^M0040_PAT_SUFFIX$/, 'suffix'],
  [/^M0050_PAT_ST$/, 'state'],
  [/^M0060_PAT_ZIP$/, 'zip_code'],
  [/^M0064_SSN/, 'ssn'],
  [/^M0066_PAT_BIRTH_DT$/, 'date_of_birth'],
  [/^NATL_PRVDR_ID$/, 'npi'],
  [/^HHA_AGENCY_ID$/, 'cms_qies_agency_id'],
  [/^STATE_CD$/, 'state'],
];

/** The input to focus for a CMS item fixed outside the chart, if one is known. */
export function fieldForCmsItem(item: string): string | undefined {
  return FIELD_FOR_ITEM.find(([pattern]) => pattern.test(item))?.[1];
}

const withFixField = (url: string, fieldId?: string) =>
  fieldId
    ? `${url}${url.includes('?') ? '&' : '?'}${FIX_FIELD_PARAM}=${encodeURIComponent(fieldId)}`
    : url;

export function clinicSettingsUrl(clinicId: string, fieldId?: string): string {
  return withFixField(
    `/admin?section=clinic-info&editClinic=${encodeURIComponent(clinicId)}`,
    fieldId
  );
}

export function patientInfoUrl(patientId: string, fieldId?: string): string {
  return withFixField(`/patients/${patientId}/info`, fieldId);
}

export function chartFixUrl(
  patientId: string,
  visitId: string,
  target: Partial<ChartFixTarget>
): string {
  const params = new URLSearchParams();
  if (target.qids?.length) params.set(QIDS, target.qids.join(','));
  if (target.items?.length) params.set(ITEMS, target.items.join(','));
  if (target.diagnoses) params.set(DIAGNOSES, '1');
  const query = params.toString();
  return `/patients/${patientId}/visits/${visitId}${query ? `?${query}` : ''}`;
}

const list = (value: string | null) => (value ?? '').split(',').filter(Boolean);

/** The fix a link asked for, or null when the URL carries none. */
export function readChartFixTarget(
  params: URLSearchParams
): ChartFixTarget | null {
  const target = {
    qids: list(params.get(QIDS)),
    items: list(params.get(ITEMS)),
    diagnoses: params.get(DIAGNOSES) === '1',
  };
  return target.qids.length || target.items.length || target.diagnoses
    ? target
    : null;
}

export function withoutChartFixTarget(params: URLSearchParams) {
  const next = new URLSearchParams(params);
  [QIDS, ITEMS, DIAGNOSES].forEach(key => next.delete(key));
  return next;
}

function castingItemIds(casting: CmsCasting | undefined): string[] {
  if (!casting) return [];
  const ids: string[] = [];
  const { itm_id: itmId, itm_id_mapping: mapping } = casting;
  if (typeof itmId === 'string') ids.push(itmId);
  if (Array.isArray(itmId)) {
    itmId.forEach(entry => entry._value && ids.push(entry._value));
  }
  Object.values(mapping ?? {}).forEach(value => value && ids.push(value));
  return ids;
}

/** CMS names an item either in full (M0069_PAT_GENDER) or by its code (A1250A). */
const sameItem = (castingId: string, item: string) =>
  castingId === item ||
  castingId.startsWith(`${item}_`) ||
  item.startsWith(`${castingId}_`);

/** The questions whose answers write any of these CMS items. */
export function qidsForCmsItems(
  schema: OASISSchema | null,
  items: string[]
): string[] {
  if (!schema || items.length === 0) return [];
  const qids: string[] = [];
  const visit = (question: Question) => {
    const ids = castingItemIds(question.cms_casting);
    if (ids.some(id => items.some(item => sameItem(id, item)))) {
      qids.push(question.qid);
    }
    Object.values(question.subitems ?? {}).forEach(subs => subs.forEach(visit));
  };
  Object.values(schema).forEach(questions => questions.forEach(visit));
  return qids;
}

const CMS_CODE = /^[A-Z]{1,2}\d{4}/;
const cmsCode = (id: string) => id.match(CMS_CODE)?.[0];

function findQuestion(schema: OASISSchema, qid: string): Question | null {
  const visit = (question: Question): Question | null => {
    if (question.qid === qid) return question;
    for (const subs of Object.values(question.subitems ?? {})) {
      for (const sub of subs) {
        const found = visit(sub);
        if (found) return found;
      }
    }
    return null;
  };
  for (const questions of Object.values(schema)) {
    for (const question of questions) {
      const found = visit(question);
      if (found) return found;
    }
  }
  return null;
}

/**
 * What a fix link points at, in words: "M1700 Cognitive Functioning", the CMS
 * item code alone without the schema, or null when neither is known.
 */
export function describeChartFixTarget(
  schema: OASISSchema | null,
  target: ChartFixTarget
): string | null {
  const itemCode = target.items.map(cmsCode).find(Boolean);
  const qids = Array.from(
    new Set([...target.qids, ...qidsForCmsItems(schema, target.items)])
  );
  const question = schema
    ? qids.map(qid => findQuestion(schema, qid)).find(Boolean)
    : null;
  if (question?.question) {
    const text = question.question.trim().replace(/[:.]$/, '');
    const code =
      itemCode ??
      [...castingItemIds(question.cms_casting), ...(question.oasis_ids ?? [])]
        .map(cmsCode)
        .find(Boolean);
    const label = code && !text.includes(code) ? `${code} ${text}` : text;
    return qids.length > 1 ? `${label} and ${qids.length - 1} more` : label;
  }
  if (itemCode) return itemCode;
  return target.diagnoses ? 'the Active Diagnosis list' : null;
}
